import React from 'react';
import {connect} from 'react-redux';
import _ from 'lodash';
import {withRouter} from 'react-router-dom';
import BigNumber from 'bignumber.js';
import LayoutContainer from '../../components/LayoutContainer';
import Title from '../../components/Title';
import Box from '../../components/Box';
import BottomImage from '../../components/BottomImage';
import {Form, Field, FormValidation, untouch} from '@nexios/frontend-forms';
import Button from '../../components/Button';
import MainContainer from '../../components/MainContainer';
import Logo from '../../components/Logo';
import Label from '../../components/Label';
import ProgressBar from '../../components/ProgressBar';
import parseAndGetBigNumber from '../../utils/parseAndGetBigNumber';
import ButtonContainer from '../../components/ButtonContainer';
import InputAmount from '../../components/InputAmount';
import FieldWrapper from '../../components/FieldWrapper';
import ErrorMessage from '../../components/ErrorMessage';
import {updateSession} from '../../reducer';
import {Helmet} from 'react-helmet';
import useScrollToTop from '../../hooks/useScrollToTop';
import Teaser from '../../components/Teaser';
import {withTheme} from 'styled-components';
import DJSBSideBar from '../../components/DJSBSideBar';
import TextToHtml from '../../components/TextToHtml';
import {wrap as wrapTitle} from '../../components/TitleWrapper';
import SelectedFrequencyContent from '../../components/SelectedFrequencyContent';
import SelectedAmountText from '../../components/SelectedAmountText';

function formatAmount(amount, options = {}) {
  const {
    showCurrencySymbol = true,
    showEuroText = false,
    showDashForWholeNumber = false
  } = options;

  const isWholeNumber = Number.isInteger(Number(amount));
  let formatted = new Intl.NumberFormat('nl-NL', {
    style: showCurrencySymbol ? 'currency' : 'decimal',
    currency: 'EUR',
    minimumFractionDigits: isWholeNumber ? 0 : 2,
    useGrouping: true
  }).format(amount);

  if (isWholeNumber && showDashForWholeNumber) {
    formatted += ',-';
  }

  if (showEuroText) {
    formatted += ' euro';
  }

  return formatted;
}

const TaxDeductible1 = ({formContext, save, history, untouchAmountFields, theme}) => {
  useScrollToTop();
  const formConfig = {
    onSubmit: () => {
      save(['showCustomAmount', 'customAmount_m', 'amount_m'], () => {
        history.push(`/doneren${history.location.search}`);
      });
    },
    onDataChanged: (ctx) => {
      const amountNewValue = _.get(ctx, ['data', 'amount_m']);
      const amountChanged = amountNewValue !== _.get(ctx, ['previousData', 'amount_m']);

      if (amountChanged && !_.isNil(amountNewValue)) {
        ctx.setValues({
          customAmount_m: null
        });

        if (formContext.customAmountDisplay === 'button') {
          ctx.setValues({
            showCustomAmount: false
          });
        }
      }

      const showCustomAmountNewValue = _.get(ctx, ['data', 'showCustomAmount']);
      const showCustomAmountChanged = showCustomAmountNewValue !== _.get(ctx, ['previousData', 'showCustomAmount']);

      if (showCustomAmountChanged && showCustomAmountNewValue === false) {
        ctx.setValues({
          customAmount_m: null
        });
      }

      if (showCustomAmountChanged && showCustomAmountNewValue === true && formContext.customAmountDisplay === 'button') {
        // Alleen bij een klik op de knop is ctx.field gevuld; bij een submit is hij leeg.
        // Zonder deze check zouden we tijdens een submit net berekende fouten wissen en
        // zou het formulier zonder bedrag verstuurd kunnen worden.
        if (ctx.field === 'showCustomAmount') {
          untouchAmountFields();
        }

        ctx.setValues({
          amount_m: null
        });
      }

      ctx.validate(true);
    },
    onInit: (ctx) => {
      const frequency = formContext.frequencies.find(f => f.value === 'yearly');
      const showInline = formContext.customAmountDisplay === 'inline' && frequency.allowCustomAmount;
      const showCustomAmount = showInline || (_.has(ctx, 'showCustomAmount') ? ctx.showCustomAmount : false);
      const customAmount = showCustomAmount ? ctx.customAmount_m : null;
      const amounts = frequency.amounts.map(a => {
        const name = formatAmount(a.amount_m, {
          showCurrencySymbol: formContext.amountDisplay === 'amount',
          showEuroText: formContext.amountDisplay === 'amountEuroText'
        });
        return {
          label: name,
          value: {
            value: a.amount_m.toString()
          }
        };
      });

      if (formContext.customAmountDisplay === 'button' && frequency.allowCustomAmount) {
        amounts.push({
          label: 'Anders',
          value: {
            value: true,
            fieldName: 'showCustomAmount'
          }
        });
      }

      ctx.setList('amounts', amounts);

      const amount = _.isNil(ctx.customAmount_m) ? ctx.amount_m || frequency.defaultAmount_m : null;

      ctx.setValues({
        showCustomAmount,
        allowCustomAmount: frequency.allowCustomAmount,
        customAmount_m: customAmount,
        amount_m: amount
      });

    },
    fields: {
      allowCustomAmount: {
        isVisible: ctx => ctx.allowCustomAmount === true && formContext.customAmountDisplay === 'toggle'
      },
      customAmount_m: {
        isVisible: (ctx) => ctx.showCustomAmount === true,
        rules: [
          {
            isValid: ctx => {
              if (!ctx.showCustomAmount) {
                return true;
              }

              const amount = _.get(ctx, ['amount_m']);
              const amountHasValue = amount !== '' && !_.isNil(amount);

              if (amountHasValue && formContext.customAmountDisplay === 'inline') {
                return true;
              }

              const frequency = formContext.frequencies.find(f => f.value === 'yearly');

              const maxAmount = BigNumber(frequency.maxAmount_m || '100000');
              const minAmount = BigNumber(frequency.minAmount_m || '1');

              const amountBn = parseAndGetBigNumber(ctx.customAmount_m);

              if (_.isNil(amountBn) || amountBn.isNaN()) {
                return true;
              }

              return amountBn.isGreaterThanOrEqualTo(minAmount) && amountBn.isLessThanOrEqualTo(maxAmount);
            },
            message: ctx => {
              const frequency = formContext.frequencies.find(f => f.value === 'yearly');

              const maxAmount = BigNumber(frequency.maxAmount_m || '100000');
              const minAmount = BigNumber(frequency.minAmount_m || '1');

              return `Vul een bedrag in tussen € ${minAmount.toFormat(2)} en € ${maxAmount.toFormat(2)}`;
            }
          },
          {
            isValid: ctx => {
              if (!ctx.showCustomAmount) {
                return true;
              }

              const amount = _.get(ctx, ['amount_m']);
              const amountHasValue = amount !== '' && !_.isNil(amount);

              if (amountHasValue && formContext.customAmountDisplay === 'inline') {
                return true;
              }

              const amountBn = parseAndGetBigNumber(ctx.customAmount_m);
              return !_.isNil(amountBn) && !amountBn.isNaN();
            },
            message: theme.showRequiredText && 'Bedrag is niet ingevuld'
          }
        ]
      },
      amount_m: {
        isVisible: (ctx) => {
          return formContext.customAmountDisplay === 'toggle' ? ctx.showCustomAmount !== true : true;
        },
        rules: [
          {
            isValid: ctx => {
              if (formContext.customAmountDisplay === 'toggle' && ctx.showCustomAmount) return true;

              const amountHasValue = !_.isNil(ctx.amount_m) && ctx.amount_m !== '';
              const customAmountHasValue = !_.isNil(ctx.customAmount_m) && ctx.customAmount_m !== '';

              return amountHasValue || customAmountHasValue;
            },
            message: theme.showRequiredText && 'Bedrag is niet ingevuld'
          }
        ]
      }
    }
  };

  return <>
    <Helmet>
      <title>{formContext.amountBrowserTitle}</title>
    </Helmet>
    <MainContainer
      backgroundImageUrl={formContext.backgroundImageUrl}
      backgroundImageTabletUrl={formContext.backgroundImageTabletUrl}
      backgroundImageMobileUrl={formContext.backgroundImageMobileUrl}
    />
    <LayoutContainer
      position={formContext.textPosition}
      header={<div>
        <div style={{float: 'left', display: 'inline-block'}}>
          <Logo url={formContext.websiteUrl} />
        </div>
      </div>}
      content={<Box position={formContext.textPosition}
        hasTeaser={!_.isNil(formContext.pageTeaserHeader) || !_.isNil(formContext.pageTeaserBodyText) || !_.isEmpty(formContext.pageTeaserCarouselItems)}>
        <Title title={formContext.step1Header}><ProgressBar progress='1' /></Title>
        <Form
          formInstanceKey='donate'
          formContext={formContext}
          formConfig={formConfig}>
          {!theme?.error?.showBelowBodyText && <FormValidation
            component={ErrorMessage}
            errorMessageTemplate={formContext.errorMessageTemplate}
            formContext={formContext}
            errorMessage='U heeft nog niet alle velden ingevuld.' />}
          <div className='widgetContent'>
            {theme?.title?.showInBody && <TextToHtml text={formContext.step1Header} wrap={wrapTitle} />}
            <TextToHtml text={formContext.step1BodyText} />
            {theme?.error?.showBelowBodyText && <FormValidation
              component={ErrorMessage}
              errorMessageTemplate={formContext.errorMessageTemplate}
              formContext={formContext}
              errorMessage='U heeft nog niet alle velden ingevuld.' />}

            <div className='marginBottom'>
              <Label label='Ik doneer per jaar' />
            </div>

            <SelectedFrequencyContent formContext={formContext} frequency='yearly' />

            <Field
              list='amounts'
              name='amount_m'
              component={(props) => {
                const list = props.list.map(a => {
                  a.fieldName = a.value.fieldName;
                  a.value = a.value.value;
                  return a;
                });

                return <div className='marginBottom amountButtons'>
                  <ButtonContainer
                    fieldName='amount_m'
                    valueName='amount_m'
                    list={list} /></div>;
              }}
            />

            <Field
              name='allowCustomAmount'
              component={() => <div className='marginBottom'>
                <Field
                  name='showCustomAmount'
                  component={({setValue, value, updateField}) => value ? null :
                    <div className='link center' onClick={() => {
                      setValue(true);
                      updateField('amount_m', null);
                    }}>
                      Of kies een ander bedrag
                    </div>}
                />
              </div>}
            />

            <div className='marginBottom'>
              <Field
                name='amount_m'
                component={() => <SelectedAmountText formContext={formContext} frequency='yearly' className='center' />}
              />
            </div>

            <div className='marginBottom'>
              {formContext.customAmountDisplay !== 'inline' && <Field
                name='customAmount_m'
                component={() => <div>Vul het bedrag in dat u wilt doneren</div>}
              />}
              <Field
                name='customAmount_m'
                placeholder={formContext.customAmountDisplay === 'inline' ? 'Of vul hier een ander bedrag in' : '€ 0,00'}
                component={FieldWrapper}
                onFocus={(e, updateField) => updateField('amount_m', null)}
                inputComponent={InputAmount}
              />
            </div>

            <div className='marginBottom'>
              <FormValidation
                type='submit'
                label={formContext.amountCallToActionLabel || 'Doneer'}
                component={Button}
              />
            </div>

            {formContext.entryCallToAction &&
              <div className='marginBottom centered'>
                {formContext.entryCallToActionUrl ? <a className='link' href={formContext.entryCallToActionUrl} rel='noopener noreferrer'>{formContext.entryCallToAction}</a> : formContext.entryCallToAction}
              </div>}
            {formContext.customAmountDisplay === 'toggle' && <Field
              name='showCustomAmount'
              component={({setValue, value, updateField}) => value ?
                <div className='link' onClick={() => {
                  setValue(false);
                  updateField('customAmount_m', null);
                }}>Terug</div> : null}
            />}
            <BottomImage url={formContext.bottomImageUrl} altText={formContext.bottomImageAltText} />
          </div>
        </Form>
      </Box>}
      sidebar={<>
        {theme?.djsb?.showDJSB && <DJSBSideBar formContext={formContext}/>}
        <Teaser header={formContext.pageTeaserHeader} body={formContext.pageTeaserBodyText} formContext={formContext} />
      </>}
      faq={formContext.frequentlyAskedQuestions}
      footer={formContext.footer}
      formContext={formContext}
      step={'1'}
    />
  </>;
};

const mapStateToProps = (state) => {
  return {
    formContext: state.reducer.formContext
  };
};

const mapDispatchToProps = (dispatch) => ({
  save: (fields, callback) => {
    dispatch(updateSession(fields, {
      isStep1Completed: true
    }, callback));
  },
  untouchAmountFields: () => {
    dispatch(untouch({instanceKey: 'donate', field: 'customAmount_m'}));
    dispatch(untouch({instanceKey: 'donate', field: 'amount_m'}));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(withTheme(TaxDeductible1)));
